import { api, getToken } from "@/lib/api/client";
import type {
  AdminRecruiterDetail,
  CommissionLine,
  CrmProspectOption,
  MyWorkspace,
  ProgramSettings,
  Proposal,
  RecruitersList,
  RecruiterStatus,
  StatementsPeriod,
  RecruiterAssignment,
} from "./types";

/**
 * Subidas con varios campos: fetch directo (igual que el comprobante de
 * liquidaciones; axios + FormData multi-campo a veces rompe el Content-Type).
 * El error imita la forma de axios para que `apiError` lo lea igual.
 */
async function postMultipart(path: string, fd: FormData): Promise<void> {
  const base = import.meta.env.VITE_API_BASE || "https://api-nexus.mixcore.club";
  const token = getToken();
  const res = await fetch(`${base}${path}`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: fd,
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw { response: { status: res.status, data } };
  }
}

/** Gestión para Mixcore: /api/nexus/recruiters (recruiters:view | manage | pay). */
const ADMIN = "/api/nexus/recruiters";
/** "Mi trabajo" del reclutador: /api/nexus/recruiter (recruiters:self). */
const SELF = "/api/nexus/recruiter";

export const recruitersAdminApi = {
  list: async (): Promise<RecruitersList> => (await api.get<RecruitersList>(ADMIN)).data,
  get: async (id: number): Promise<AdminRecruiterDetail> => (await api.get<AdminRecruiterDetail>(`${ADMIN}/${id}`)).data,
  create: async (payload: {
    name: string;
    email: string;
    phone?: string;
    rut?: string;
    agreementSignedOn?: string;
    agreementVersion?: string;
    internalNotes?: string;
    sendWelcome: boolean;
  }): Promise<{ recruiter: { id: number }; linked: boolean }> => (await api.post(ADMIN, payload)).data,
  update: async (
    id: number,
    payload: Partial<{
      name: string;
      phone: string;
      rut: string;
      agreementSignedOn: string;
      agreementVersion: string;
      internalNotes: string;
      status: RecruiterStatus;
      endedOn: string;
      endReason: string;
    }>,
  ): Promise<void> => {
    await api.patch(`${ADMIN}/${id}`, payload);
  },
  sync: async (id: number): Promise<void> => {
    await api.post(`${ADMIN}/${id}/sync`);
  },
  resendWelcome: async (id: number): Promise<void> => {
    await api.post(`${ADMIN}/${id}/welcome`);
  },
  searchProspects: async (q: string): Promise<{ prospects: CrmProspectOption[] }> =>
    (await api.get(`${ADMIN}/prospects`, { params: { q } })).data,
  assignProducer: async (id: number, payload: { prospectId: number; registeredOn?: string }): Promise<void> => {
    await api.post(`${ADMIN}/${id}/producers`, payload);
  },
  proposals: async (): Promise<{ proposals: Proposal[] }> => (await api.get(`${ADMIN}/proposals`)).data,
  assignments: async (): Promise<{ assignments: RecruiterAssignment[] }> => (await api.get(`${ADMIN}/assignments`)).data,
  decideProducer: async (
    rpId: number,
    payload: { action: "accept" | "reject" | "release" | "redate"; prospectId?: number; note?: string; registeredOn?: string },
  ): Promise<void> => {
    await api.patch(`${ADMIN}/producers/${rpId}`, payload);
  },
  reviewEvent: async (
    reId: number,
    payload: { action: "approve" | "observe" | "retain" | "reopen"; note?: string },
  ): Promise<void> => {
    await api.patch(`${ADMIN}/events/${reId}/review`, payload);
  },
  createAdjustment: async (id: number, payload: { amount: number; description: string }): Promise<{ commission: CommissionLine }> =>
    (await api.post(`${ADMIN}/${id}/adjustments`, payload)).data,
  voidAdjustment: async (cid: number): Promise<void> => {
    await api.delete(`${ADMIN}/adjustments/${cid}`);
  },
  statements: async (period?: string): Promise<StatementsPeriod> =>
    (await api.get(`${ADMIN}/statements`, { params: period ? { period } : {} })).data,
  generateStatements: async (period: string): Promise<{ created: number; updated: number; skipped: { recruiterId: number; reason: string }[] }> =>
    (await api.post(`${ADMIN}/statements/generate`, { period })).data,
  observeInvoice: async (sid: number, note: string): Promise<void> => {
    await api.post(`${ADMIN}/statements/${sid}/observe-invoice`, { note });
  },
  payStatement: async (sid: number, reference: string, file: File | null): Promise<void> => {
    const fd = new FormData();
    fd.append("reference", reference);
    if (file) fd.append("file", file, file.name);
    await postMultipart(`${ADMIN}/statements/${sid}/pay`, fd);
  },
  settings: async (): Promise<{ settings: ProgramSettings }> => (await api.get(`${ADMIN}/settings`)).data,
  saveSettings: async (payload: Partial<ProgramSettings>): Promise<{ settings: ProgramSettings }> =>
    (await api.put(`${ADMIN}/settings`, payload)).data,
};

export const recruiterSelfApi = {
  me: async (): Promise<MyWorkspace> => (await api.get<MyWorkspace>(`${SELF}/me`)).data,
  propose: async (payload: { name: string; contactEmail?: string; phone?: string; note?: string }): Promise<void> => {
    await api.post(`${SELF}/proposals`, payload);
  },
  logActivity: async (rpId: number, payload: { kind: "reunion" | "respuesta" | "avance"; note: string }): Promise<void> => {
    await api.post(`${SELF}/producers/${rpId}/activity`, payload);
  },
  updateItem: async (
    reId: number,
    key: string,
    payload: { status: "done" | "pending" | "na"; note?: string; reason?: "force_majeure" | "not_requested" },
  ): Promise<void> => {
    await api.patch(`${SELF}/events/${reId}/items/${key}`, payload);
  },
  submitReport: async (reId: number, payload: { worked: string; failed: string; producerAsked: string }): Promise<void> => {
    await api.post(`${SELF}/events/${reId}/report`, payload);
  },
  declareSimultaneous: async (reId: number, payload: { otherEventId: number; attendedId: number; note?: string }): Promise<void> => {
    await api.post(`${SELF}/events/${reId}/simultaneous`, payload);
  },
  uploadInvoice: async (sid: number, invoiceNumber: string, file: File): Promise<void> => {
    const fd = new FormData();
    fd.append("invoiceNumber", invoiceNumber);
    fd.append("file", file, file.name);
    await postMultipart(`${SELF}/statements/${sid}/invoice`, fd);
  },
  /** Términos y Condiciones para Colaboradores (backend /api/terms, en todos los servicios). */
  termsStatus: async (): Promise<{
    pending: string[];
    documents: { key: string; title: string; version: string; url: string; accepted: boolean; previouslyAccepted: string | null }[];
    privacyUrl: string;
  }> => (await api.get("/api/terms/status", { params: { scope: "recruiter" } })).data,
  acceptTerms: async (documents: string[]): Promise<void> => {
    await api.post("/api/terms/accept", { documents, source: "nexus" });
  },
  updatePayment: async (payload: {
    bankName?: string;
    accountType?: string;
    accountNumber?: string;
    holderName?: string;
    holderRut?: string;
    rut?: string;
    phone?: string;
  }): Promise<void> => {
    await api.patch(`${SELF}/payment`, payload);
  },
};

/** Mensaje de error legible del backend (o uno genérico). */
export function apiError(err: unknown, fallback = "Algo falló. Inténtalo de nuevo."): string {
  const e = err as { response?: { data?: { message?: string; error?: string } } };
  return e?.response?.data?.message || e?.response?.data?.error || fallback;
}
